import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import './DecksPage.css';

import { useAuth } from '../../hook/useAuth';
import { useDeck } from '../../hook/useDeck';
import { DndContext } from '../../components/DndKit';
import Layout from '../../layouts/layouts';
import ConfirmModal from '../../components/Modals/ConfirmModal/ConfirmModal';
import DeckConstructorPanel from './DeckConstructorPanel/DeckConstructorPanel';
import DeckListPanel from './DeckListPanel/DeckListPanel';
import ModifyDeckModal from '../../components/Modals/ModifyDeckModal/ModifyDeckModal';

/**
 * Deck construction page
 * @component
 */
const DecksPage = ({ title, layout }) => {
	const navigate = useNavigate();
	const { authenticated, signin } = useAuth();
	const { activeDeck, addDeck, updateDeck, deleteDeck } = useDeck();

	const [showAddModal, setShowAddModal] = useState(false);
	const [showModifyModal, setShowModifyModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	// Refs to store DeckConstructorPanel's drag functions
	const deckConstructorDragHandlerRef = useRef(null);
	const deckConstructorRenderOverlayRef = useRef(null);

	useEffect(() => {
		console.log('Initialization: DecksPage');
	}, []);

	// Drag overlay renderer for DndContext - delegates to DeckConstructorPanel
	const renderDragOverlay = (activeId, activeData) => {
		if (deckConstructorRenderOverlayRef.current) {
			return deckConstructorRenderOverlayRef.current(activeId, activeData);
		}

		// Fallback if DeckConstructorPanel hasn't registered yet
		return <div>Dragging...</div>;
	};

	// Drag end handler for DndContext - delegates to DeckConstructorPanel
	const handleDragEnd = (result) => {
		if (deckConstructorDragHandlerRef.current) {
			deckConstructorDragHandlerRef.current(result);
		}
	};

	// return Layout(props.layout, props, (
	return (
		<Layout layout={layout} title={title}>
			<DndContext renderDragOverlay={renderDragOverlay} onDragEnd={handleDragEnd}>
				<div className="decks">
					<DeckListPanel
						onAddDeckReq={handleAddDeck}
						onDeleteDeckReq={handleDeleteDeck}
						onModifyDeckReq={handleModifyDeck}
						onSelectDeck={handleSelectDeck}
					/>
					{activeDeck ? (
						<DeckConstructorPanel
							dragHandlerRef={deckConstructorDragHandlerRef}
							renderOverlayRef={deckConstructorRenderOverlayRef}
						/>
					) : null}
					<ModifyDeckModal deck={null} isOpen={showAddModal} title="Add Deck" onResult={processAddDeck} />
					<ModifyDeckModal
						deck={activeDeck}
						isOpen={showModifyModal}
						title="Modify Deck"
						onResult={processModifyDeck}
					/>
					<ConfirmModal
						className="delete-deck"
						defaultYes={false}
						isOpen={showDeleteModal}
						title="Delete deck"
						onResult={processDeleteStorage}
					>
						<>
							Are you sure you want to delete the deck?
							<div className="deck-name">{activeDeck ? activeDeck.name : ''}</div>
						</>
					</ConfirmModal>
				</div>
			</DndContext>
		</Layout>
	);

	function processAddDeck(result) {
		setShowAddModal(false);
		if (result.status) {
			// console.log('processAddDeck', result);
			addDeck(
				{
					name: result.data.name,
					note: result.data.note,
				},
				(result) => {
					if (result) navigate(getCurrentURL(result));
				}
			);
		}
	}

	function processModifyDeck(result) {
		// console.log('processModifyDeck', result);
		setShowModifyModal(false);
		if (result.status) {
			updateDeck(
				activeDeck,
				{
					name: result.data.name,
					note: result.data.note,
				},
				(result) => {
					if (result) navigate(getCurrentURL(result));
				}
			);
		}
	}

	function processDeleteStorage(result) {
		setShowDeleteModal(false);
		if (result) {
			deleteDeck(activeDeck, (result) => {
				if (result) navigate(getCurrentURL(null));
			});
		}
	}

	function handleSelectDeck(deck) {
		navigate(getCurrentURL(deck));
	}

	function handleAddDeck() {
		if (authenticated) {
			setShowAddModal(true);
		} else {
			signin();
		}
	}

	function handleDeleteDeck(deck) {
		if (authenticated) {
			navigate(getCurrentURL(deck));
			setShowDeleteModal(true);
		} else {
			signin();
		}
	}

	function handleModifyDeck(deck) {
		if (authenticated) {
			navigate(getCurrentURL(deck));
			setShowModifyModal(true);
		} else {
			signin();
		}
	}

	// Get URL for current page
	function getCurrentURL(deck) {
		let url = '/decks';
		if (deck) {
			url = '/decks/' + deck.id;
		}
		return url;
	}
};

DecksPage.propTypes = {
	// Page title for the browser tab
	title: PropTypes.string.isRequired,
	// Page Layout
	layout: PropTypes.string.isRequired,
};

export default DecksPage;
